import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchPatientStories,
  editPatientStory,
  updatePatientStory,
  trashPatientStory,
} from "../../Adapters/ApiAdapter";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseImage_path } from "../../variables/Variables";
import ReactQuill from "react-quill";

const PatientStoriesList = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editStory, setEditStory] = useState(null); // Story to edit
  const [deleteStoryId, setDeleteStoryId] = useState(null); // Story to delete
  const [loadingAction, setLoadingAction] = useState(false); // Loading state for actions

  // Fetch stories on component mount
  useEffect(() => {
    const loadStories = async () => {
      try {
        const response = await fetchPatientStories();
        if (response?.data) {
          setStories((response.data).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
          );
        } else {
          setError("Failed to fetch patient stories.");
        }
      } catch (err) {
        setError("An error occurred while fetching patient stories.");
      } finally {
        setLoading(false);
      }
    };

    loadStories();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editPatientStory(id);
      setEditStory(response.data);
    } catch (err) {
      console.error("Error loading edit data:", err);
      toast.error("Failed to load story for editing.");
    }
  };

  const handleSaveEdit = async () => {
    const formData = new FormData();

    formData.append("name", editStory.name);
    formData.append("heading", editStory.heading);
    formData.append("review", editStory.review);

    if (editStory.blog_img instanceof File) {
      formData.append("blog_img", editStory.blog_img);
    }

    const videoLink = editStory.video_link?.trim();
    if (videoLink && isValidURL(videoLink)) {
      formData.append("video_link", videoLink);
    }

    setLoadingAction(true);

    try {
      const response = await updatePatientStory(editStory.id, formData);
      console.log("Edit response:", response);
      toast.success("Patient story updated successfully!");
      setEditStory(null);
      setStories((prev) =>
        prev.map((story) => (story.id === editStory.id ? response.data : story))
      );
    } catch (err) {
      console.error("Error saving edit:", err);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setLoadingAction(false);
    }
  };

  const isValidURL = (string) => {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" +
      "((([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,})|" +
      "localhost|" +
      "((\\d{1,3}\\.){3}\\d{1,3}))" +
      "(\\:\\d+)?(\\/[-a-zA-Z0-9@:%_\\+.~#?&//=]*)?$",
      "i"
    );
    return !!pattern.test(string);
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);

    try {
      const response = await trashPatientStory(id);
      console.log("Delete response:", response);
      toast.success("Patient story deleted successfully!");
      setStories((prev) => prev.filter((story) => story.id !== id));
      setDeleteStoryId(null); // Close dialog
    } catch (err) {
      console.error("Error deleting story:", err);
      toast.error("Failed to delete story. Please try again.");
    } finally {
      setLoadingAction(false);
    }
  };
  const stripHtml = (html) => html.replace(/<[^>]+>/g, "").trim();

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "heading", headerName: "Heading", width: 250 },
    { field: "review", headerName: stripHtml("Review"), width: 300 },
    { field: "video_link", headerName: "Video Link", width: 300 },
    {
      field: "blog_img",
      headerName: "Image",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <img
            src={`${baseImage_path}/${params.value}`} // Append baseImage_path
            alt="Blog"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 200,
      valueFormatter: (params) =>
        dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteStoryId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Patient Stories List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={stories} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editStory && (
        <Dialog open onClose={() => setEditStory(null)}>
          <DialogTitle>Edit Patient Story</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={editStory.name}
              onChange={(e) =>
                setEditStory({ ...editStory, name: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editStory.heading}
              onChange={(e) =>
                setEditStory({ ...editStory, heading: e.target.value })
              }
            />

            <Typography variant="body1">Review</Typography>
            <ReactQuill
              theme="snow"
              value={editStory.review}
              onChange={(e) => setEditStory({ ...editStory, review: e })}
              style={{ height: "200px", marginBottom: "50px" }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Video Link"
              value={editStory.video_link}
              onChange={(e) =>
                setEditStory({ ...editStory, video_link: e.target.value })
              }
            />
            <input
              style={{ marginTop: "10px" }}
              type="file"
              accept="image/*"
              onChange={(e) =>
                setEditStory({ ...editStory, blog_img: e.target.files[0] })
              }
            />

            {editStory.blog_img && (
              <Box mt={2}>
                <Typography variant="subtitle1">Current Image:</Typography>
                <img
                  src={
                    typeof editStory.blog_img === "string"
                      ? `${baseImage_path}/${editStory.blog_img}`
                      : URL.createObjectURL(editStory.blog_img)
                  }
                  alt="Blog"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditStory(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteStoryId && (
        <Dialog open onClose={() => setDeleteStoryId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this patient story?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteStoryId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteStoryId)}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PatientStoriesList;
